/* eslint-disable react/prop-types */
import { Clock, Phone, MessageSquare, Mail, CalendarPlus } from "lucide-react";

/**
 * LeadTimelineSection — Phase 7 Sub-iter 7B.
 *
 * Renders the unified timeline (calls, meetings, comments, emails,
 * WhatsApp) for a lead.  Read-only.
 */

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 31) return `${days}d ago`;
  return d.toLocaleDateString("en-GB");
}

function TimelineRow({ item }) {
  const kind = item.kind || "comment";
  const palette = {
    call: { color: "#1FB6C1", icon: Phone, label: "Call" },
    meeting: { color: "#A78BFA", icon: CalendarPlus, label: "Meeting" },
    comment: { color: "#94A3B8", icon: MessageSquare, label: "Comment" },
    email: { color: "#F59E0B", icon: Mail, label: "Email" },
    whatsapp: { color: "#22C55E", icon: MessageSquare, label: "WhatsApp" },
  }[kind] || { color: "#94A3B8", icon: MessageSquare, label: kind };
  const Icon = palette.icon;
  return (
    <div className="flex gap-3" data-testid={`lead-timeline-${kind}-${item.id}`}>
      <div className="flex flex-col items-center pt-1">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center border"
          style={{ borderColor: `${palette.color}55`, background: `${palette.color}15`, color: palette.color }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="w-px flex-1 bg-white/5 my-1" />
      </div>
      <div className="flex-1 pb-4">
        <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-wider">
          <span className="font-bold" style={{ color: palette.color }}>{palette.label}</span>
          {item.by_user_name && <span className="text-[#1fb6c1]">{item.by_user_name}</span>}
          <span className="text-[#647087]">{timeAgo(item.ts)}</span>
        </div>
        {item.title && item.title !== palette.label && (
          <div className="text-sm font-medium mt-0.5 truncate">{item.title}</div>
        )}
        {item.body && (
          <div className="text-sm whitespace-pre-wrap mt-1 text-[#cbd5e1]">{item.body}</div>
        )}
        {item.duration_seconds > 0 && (
          <div className="text-[10px] text-[#647087] mt-1 inline-flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" /> {Math.round(item.duration_seconds / 60)}m talk-time
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeadTimelineSection({ items }) {
  return (
    <div className="hv-card p-4" data-testid="lead-detail-timeline">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-[#1fb6c1]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#a0aabf] font-bold">
          Timeline {items.length > 0 && <span className="text-[#1fb6c1]">· {items.length}</span>}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#647087] text-center py-6">
          No activity yet — drop a comment, log a call from the floating button, or schedule a meeting.
        </div>
      ) : (
        <div>
          {items.map((it) => (
            <TimelineRow key={`${it.kind}-${it.id}`} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}
